<script lang="ts">
	/**
	 * Edits an IP allowlist: one row per address or CIDR range, each saying on its own line what it
	 * admits.
	 *
	 * It replaced a tag input that took any string. The server cannot match an entry it cannot
	 * parse, so a typo there was stored and simply never matched — the application was locked out
	 * of every endpoint while the screen showed its allowlist as configured. The server now refuses
	 * such an entry by name; this is the half that says so before Save, on the row it is about.
	 *
	 * Stating the coverage is the other half, and the one a refusal cannot provide: `/16` where `/24`
	 * was meant is a *valid* entry that opens the endpoint to 65,536 addresses instead of 256, and
	 * nothing about the string looks wrong.
	 *
	 * Bind `valid` to gate the surrounding form's Save. An empty list is valid and means "any".
	 */

	import { Button, Input } from 'flowbite-svelte';
	import { parseCIDRRange, type CIDRRange } from '../Utilities';

	/** Two-way bindable. Blank rows are kept while editing and dropped by the caller on save. */
	export let values: string[] = [];
	/** Two-way bindable: false while any non-blank row cannot be parsed. */
	export let valid: boolean = true;
	export let disabled: boolean = false;
	/** Prefix for each row's input id, so a label can target the first one. */
	export let id: string = 'cidr';

	$: parsed = values.map(entry => (entry.trim() === '' ? undefined : parseCIDRRange(entry)));
	$: valid = parsed.every(range => range !== null);

	function fnCount(range: CIDRRange): string {
		if (range.family === 6 && range.prefix < 64) {
			return `2^${128 - range.prefix} addresses`;
		}
		const count = range.count;
		return count === 1n ? '1 address' : `${count.toLocaleString()} addresses`;
	}

	function fnDescribe(range: CIDRRange): string {
		const family = range.family === 4 ? 'IPv4' : 'IPv6';
		if (range.count === 1n) return `${family} · this address only`;
		const span = range.first !== null && range.last !== null ? ` · ${range.first} – ${range.last}` : ` · ${range.network}/${range.prefix}`;
		return `${family} · ${fnCount(range)}${span}`;
	}

	function fnAdd(): void {
		values = [...values, ''];
	}

	function fnRemove(index: number): void {
		values = values.filter((_unused, position) => position !== index);
	}
</script>

<div class="space-y-2">
	{#each values as _value, index (index)}
		{@const range = parsed[index]}
		<div>
			<div class="flex items-center gap-2">
				<Input
					id="{id}-{index}"
					type="text"
					size="sm"
					class="font-mono"
					placeholder="203.0.113.0/24"
					spellcheck="false"
					autocomplete="off"
					aria-invalid={range === null}
					aria-describedby="{id}-{index}-note"
					bind:value={values[index]}
					{disabled}
				/>
				<Button
					color="none"
					class="!p-2 text-default hover:text-red-600 shrink-0"
					aria-label="Remove {values[index] || 'this row'}"
					{disabled}
					on:click={() => fnRemove(index)}
				>
					<i class="fa-solid fa-xmark" aria-hidden="true" />
				</Button>
			</div>
			<p id="{id}-{index}-note" class="text-xs mt-1 {range === null ? 'text-red-600 dark:text-red-400' : 'text-default'}">
				{#if range === undefined}
					An address such as 203.0.113.4, or a range such as 203.0.113.0/24 or 2001:db8::/32.
				{:else if range === null}
					Not an IP address or CIDR range. The application could never match it.
				{:else}
					{fnDescribe(range)}
					{#if range.hostBitsSet}
						<span class="block text-amber-700 dark:text-amber-400">
							Read as {range.network}/{range.prefix} — the bits after the prefix are ignored.
						</span>
					{/if}
					{#if range.prefix === 0}
						<span class="block text-amber-700 dark:text-amber-400">
							Admits every {range.family === 4 ? 'IPv4' : 'IPv6'} address, which is the same as no restriction.
						</span>
					{/if}
				{/if}
			</p>
		</div>
	{/each}

	{#if values.length === 0}
		<p class="text-sm text-default">Any address may call. Add a range to narrow it.</p>
	{/if}

	<Button color="alternative" size="xs" {disabled} on:click={fnAdd}>
		<i class="fa-solid fa-plus mr-2" aria-hidden="true" />Add address or range
	</Button>
</div>
